import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type CallFlow, type Greeting, type FlowNode } from "../api.ts";
import type { Graph } from "./types";
import { ensurePositions, ensureSeqs, autoLayout, boundingBox, flowIsAnswerable } from "./layout.ts";
import { Canvas } from "./Canvas.tsx";
import { Inspector } from "./Inspector.tsx";
import { Palette, blankNode } from "./Palette.tsx";

// Top-level page. Owns: data fetch, the active flow's local Graph,
// dirty tracking, save lifecycle. Inspector + canvas are children.

/** Cross-component dirty flag: AdminDashboard reads it to warn before
 *  a sidebar navigation unmounts the editor with unsaved changes
 *  (beforeunload only covers closing the browser tab). */
export const flowEditorDirty = { current: false };

const KIND_FR: Record<FlowNode["kind"], string> = {
  start: "Démarrage",
  play: "Diffuser",
  collect: "Demander un numéro",
  menu: "Menu DTMF",
  ring: "Sonner les agents",
  voicemail: "Répondeur",
  hangup: "Raccrocher",
};

export function FlowEditor(props: { token: string }) {
  const [flows, setFlows] = useState<CallFlow[] | null>(null);
  const [greetings, setGreetings] = useState<Greeting[] | null>(null);
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  // Manual override forced by the admin — bypasses the schedule cascade.
  // null = schedule-driven (normal mode).
  const [overrideFlowId, setOverrideFlowId] = useState<string | null>(null);
  const [graph, setGraph] = useState<Graph | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // "Pick on canvas" linking mode: while set, the next click on a
  // block wires it as the target of `field` on `sourceId` (instead of
  // going through the Inspector dropdown). Escape / background click
  // cancels.
  const [linking, setLinking] = useState<{
    sourceId: string;
    field: "next" | "onTimeout" | "onInvalid" | "onNoAnswer" | "onNoOneOnline" | "branch";
    branchKey?: string;
    label: string;
  } | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [flowsRes, greetingsRes, overrideRes] = await Promise.all([
        api.listFlows(props.token),
        api.listGreetings(props.token),
        api.getActiveOverride(props.token),
      ]);
      setFlows(flowsRes.flows);
      setGreetings(greetingsRes.greetings);
      setOverrideFlowId(overrideRes.flowId);
      setActiveFlowId((cur) => cur ?? flowsRes.flows[0]?.id ?? null);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [props.token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeFlow = useMemo(
    () => flows?.find((f) => f.id === activeFlowId) ?? null,
    [flows, activeFlowId],
  );

  // Reload local Graph state when the SELECTED flow changes — but
  // not on every parent refresh (which would clobber unsaved edits).
  // We key the load on activeFlowId only; the user explicitly saving
  // updates savedSnapshot in `save()` and a server-side roundtrip
  // doesn't trigger this effect because activeFlowId stays the same.
  const loadKey = activeFlowId ?? "";
  useEffect(() => {
    if (!activeFlow) {
      setGraph(null);
      setSavedSnapshot("");
      setSelectedId(null);
      return;
    }
    const raw = activeFlow.graph as unknown as Partial<Graph>;
    const initial: Graph = ensureSeqs(
      ensurePositions({
        rootId: raw.rootId ?? "n_start",
        nodes: raw.nodes ?? {},
        positions: (raw.positions as Graph["positions"]) ?? {},
        seqs: (raw.seqs as Graph["seqs"]) ?? {},
      }),
    );
    setGraph(initial);
    setSavedSnapshot(JSON.stringify(initial));
    setSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  const dirty = graph != null && JSON.stringify(graph) !== savedSnapshot;
  const validationProblems = useMemo(
    () => (graph ? validate(graph) : []),
    [graph],
  );

  // Expose dirtiness to the dashboard shell (sidebar navigation guard).
  useEffect(() => {
    flowEditorDirty.current = dirty;
    return () => {
      flowEditorDirty.current = false;
    };
  }, [dirty]);

  // #25: warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent): string => {
      e.preventDefault();
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // #23 / #24: keyboard shortcuts. Delete = remove selected block,
  // Escape = close inspector. Ignore when typing in form fields.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return;
      if (e.key === "Escape") {
        if (linking) setLinking(null);
        else setSelectedId(null);
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        if (graph && selectedId === graph.rootId) return;
        if (selectedId) onDeleteNode(selectedId);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, graph, linking]);

  // #12: warn before switching flows mid-edit.
  function tryActivate(id: string | null): void {
    if (id === activeFlowId) return;
    if (dirty) {
      const ok = confirm("Modifications non enregistrées. Changer de flux et perdre les modifications ?");
      if (!ok) return;
    }
    setActiveFlowId(id);
  }

  function patchGraph(fn: (g: Graph) => Graph): void {
    setGraph((g) => (g ? fn(g) : g));
  }

  function onMove(id: string, x: number, y: number): void {
    patchGraph((g) => ({ ...g, positions: { ...g.positions, [id]: { x, y } } }));
  }

  function onUpdateNode(id: string, next: FlowNode): void {
    patchGraph((g) => ({ ...g, nodes: { ...g.nodes, [id]: next } }));
  }

  function onDeleteNode(id: string): void {
    patchGraph((g) => {
      if (id === g.rootId) return g; // never delete the start node
      // #17: when the deleted block is a single-out block (start /
      // play / collect), auto-rewire incoming edges to its `next`
      // instead of orphaning them. Multi-out blocks (menu / ring)
      // and leaves (voicemail / hangup) just clear references —
      // there's no single sensible replacement.
      const removed = g.nodes[id];
      const replacement =
        removed && (removed.kind === "start" || removed.kind === "play" || removed.kind === "collect")
          ? removed.next
          : "";
      const remainingNodes: Record<string, FlowNode> = {};
      for (const [nid, n] of Object.entries(g.nodes)) {
        if (nid === id) continue;
        remainingNodes[nid] = retargetReferences(n, id, replacement);
      }
      const remainingPositions = { ...g.positions };
      delete remainingPositions[id];
      return { ...g, nodes: remainingNodes, positions: remainingPositions };
    });
    setSelectedId((s) => (s === id ? null : s));
  }

  function onAddNode(kind: FlowNode["kind"]): void {
    if (!graph) return;
    const id = `n_${Math.random().toString(36).slice(2, 8)}`;
    const node = blankNode(kind);
    const bb = boundingBox(graph.positions);
    const x = (bb.minX + bb.maxX) / 2 - 110;
    const y = bb.maxY + 40;
    patchGraph((g) => ({
      ...g,
      nodes: { ...g.nodes, [id]: node },
      positions: { ...g.positions, [id]: { x, y } },
      // Numbers are monotonic per flow (stale entries kept on delete),
      // so a freshly added block never inherits a deleted block's #N.
      seqs: { ...g.seqs, [id]: Math.max(0, ...Object.values(g.seqs)) + 1 },
    }));
    setSelectedId(id);
  }

  /** Enter "pick on canvas" mode for one outgoing edge of the
   *  selected block. The next block clicked becomes the target. */
  function startLink(
    field: NonNullable<typeof linking>["field"],
    label: string,
    branchKey?: string,
  ): void {
    if (!selectedId) return;
    setLinking({ sourceId: selectedId, field, branchKey, label });
  }

  function applyLink(targetId: string): void {
    const l = linking;
    if (!l) return;
    patchGraph((g) => {
      const n = g.nodes[l.sourceId];
      if (!n) return g;
      let next: FlowNode = n;
      if (l.field === "branch") {
        if (n.kind !== "menu" || !l.branchKey) return g;
        next = { ...n, branches: { ...n.branches, [l.branchKey]: targetId } };
      } else if (l.field === "next") {
        if (n.kind !== "start" && n.kind !== "play" && n.kind !== "collect") return g;
        next = { ...n, next: targetId };
      } else if (l.field === "onTimeout" || l.field === "onInvalid") {
        if (n.kind !== "menu") return g;
        next = { ...n, [l.field]: targetId };
      } else {
        if (n.kind !== "ring") return g;
        next = { ...n, [l.field]: targetId };
      }
      return { ...g, nodes: { ...g.nodes, [l.sourceId]: next } };
    });
    setLinking(null);
    // Keep the source block selected so the Inspector shows the result.
    setSelectedId(l.sourceId);
  }

  /** Canvas click router: normal mode selects; linking mode wires. */
  function handleCanvasSelect(id: string | null): void {
    if (linking) {
      if (id && id !== linking.sourceId) applyLink(id);
      else setLinking(null); // background or self-click cancels
      return;
    }
    setSelectedId(id);
  }

  function relayout(): void {
    if (!graph) return;
    patchGraph((g) => ({ ...g, positions: autoLayout(g.rootId, g.nodes) }));
  }

  /** Remove every block that's NOT reachable from the root. Useful
   *  when test edits leave dangling blocks (and to avoid the "why are
   *  there grey islands" confusion). */
  function pruneOrphans(): void {
    if (!graph) return;
    const reachable = new Set<string>();
    const queue = [graph.rootId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (reachable.has(id)) continue;
      reachable.add(id);
      const n = graph.nodes[id];
      if (!n) continue;
      switch (n.kind) {
        case "start":
        case "play":
        case "collect":
          if (n.next) queue.push(n.next);
          break;
        case "menu":
          for (const t of Object.values(n.branches)) if (t) queue.push(t);
          if (n.onTimeout) queue.push(n.onTimeout);
          if (n.onInvalid) queue.push(n.onInvalid);
          break;
        case "ring":
          if (n.onAnswer) queue.push(n.onAnswer);
          if (n.onNoAnswer) queue.push(n.onNoAnswer);
          if (n.onNoOneOnline) queue.push(n.onNoOneOnline);
          break;
        case "voicemail":
        case "hangup":
          break;
      }
    }
    const orphans = Object.keys(graph.nodes).filter((id) => !reachable.has(id));
    if (orphans.length === 0) return;
    if (!confirm(`Supprimer ${orphans.length} bloc(s) orphelin(s) (non reliés au démarrage) ?`)) return;
    patchGraph((g) => {
      const nodes: Record<string, FlowNode> = {};
      const positions: Graph["positions"] = {};
      for (const id of Object.keys(g.nodes)) {
        if (reachable.has(id)) {
          nodes[id] = g.nodes[id]!;
          if (g.positions[id]) positions[id] = g.positions[id]!;
        }
      }
      return { ...g, nodes, positions };
    });
  }

  async function save(): Promise<void> {
    if (!graph || !activeFlow) return;
    setBusy(true);
    try {
      // Backend ignores `positions` (extra jsonb fields) but stores
      // them, so the layout round-trips and survives reloads.
      await api.patchFlow(props.token, activeFlow.id, { graph: graph as never });
      setSavedSnapshot(JSON.stringify(graph));
      // Pull the canonical server view but DON'T reload graph state
      // (loadKey didn't change → useEffect won't fire).
      const flowsRes = await api.listFlows(props.token);
      setFlows(flowsRes.flows);
    } catch (err) {
      setError(`save: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  // ─── Flow chrome ────────────────────────────────────────────
  async function newFlow(): Promise<void> {
    // Creating a flow switches the editor to it — same loss vector as
    // tryActivate, same guard.
    if (dirty && !confirm("Modifications non enregistrées. Créer un autre flux et les perdre ?")) return;
    const name = prompt("Nom du nouveau flux :");
    if (!name) return;
    setBusy(true);
    try {
      const created = await api.createFlow(props.token, {
        name,
        graph: {
          rootId: "n_start",
          nodes: {
            n_start: { kind: "start", next: "n_done" },
            n_done: { kind: "hangup" },
          },
        } as never,
      });
      await refresh();
      setActiveFlowId(created.id);
    } catch (err) {
      setError(`create: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function duplicate(): Promise<void> {
    if (!activeFlow) return;
    if (dirty && !confirm("Modifications non enregistrées. Dupliquer et les perdre ? (Enregistrez d'abord pour dupliquer la dernière version.)")) return;
    const name = prompt("Nom de la copie :", `${activeFlow.name} (copie)`);
    if (!name) return;
    setBusy(true);
    try {
      const created = await api.createFlow(props.token, {
        name,
        graph: activeFlow.graph as never,
      });
      await refresh();
      setActiveFlowId(created.id);
    } catch (err) {
      setError(`duplicate: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function rename(): Promise<void> {
    if (!activeFlow) return;
    const name = prompt("Renommer ce flux :", activeFlow.name);
    if (!name || name === activeFlow.name) return;
    setBusy(true);
    try {
      await api.patchFlow(props.token, activeFlow.id, { name });
      await refresh();
    } catch (err) {
      setError(`rename: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  // NB: the "fallback" (is_default) flag and manual activation are
  // managed in the Programmation tab — the editor only designs
  // graphs. The override banner below stays as a warning (with its
  // clear button) so nobody edits thinking the schedule applies.

  async function clearOverride(): Promise<void> {
    setBusy(true);
    try {
      await api.setActiveOverride(props.token, null);
      await refresh();
    } catch (err) {
      setError(`override: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!activeFlow || (flows?.length ?? 0) <= 1) return;
    if (!confirm(`Supprimer le flux « ${activeFlow.name} » ?`)) return;
    setBusy(true);
    try {
      await api.deleteFlow(props.token, activeFlow.id);
      setActiveFlowId(null);
      await refresh();
    } catch (err) {
      setError(`delete: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const selectedNode = graph && selectedId ? graph.nodes[selectedId] : null;
  const targets = useMemo(() => {
    if (!graph || !selectedId) return [];
    return Object.entries(graph.nodes)
      .filter(([id]) => id !== selectedId)
      .sort(([a], [b]) => (graph.seqs[a] ?? 0) - (graph.seqs[b] ?? 0))
      .map(([id, n]) => ({ id, label: `#${graph.seqs[id] ?? "?"} · ${KIND_FR[n.kind]}` }));
  }, [graph, selectedId]);

  return (
    <div className="flow-page">
      <section className="card flow-card">
        <div className="card-header">
          <h3>Flux d'appel</h3>
          <div className="actions">
            <select
              value={activeFlowId ?? ""}
              onChange={(e) => tryActivate(e.target.value || null)}
              disabled={busy}
            >
              {flows?.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.isDefault ? "⭐ " : ""}{f.name}{f.isDefault ? " — repli par défaut" : ""}
                </option>
              ))}
            </select>
            <button className="ghost" type="button" onClick={() => void newFlow()} disabled={busy}>
              + Nouveau
            </button>
            <button className="ghost" type="button" onClick={() => void duplicate()} disabled={busy || !activeFlow}>
              Dupliquer
            </button>
            <button className="ghost" type="button" onClick={() => void rename()} disabled={busy || !activeFlow}>
              Renommer
            </button>
            <button
              className="ghost"
              type="button"
              onClick={() => void remove()}
              disabled={busy || !activeFlow || (flows?.length ?? 0) <= 1}
            >
              Supprimer
            </button>
            <span className="flow-card__divider" aria-hidden="true" />
            <button className="ghost" type="button" onClick={pruneOrphans} disabled={!graph || busy}>
              🧹 Nettoyer
            </button>
            <button className="ghost" type="button" onClick={relayout} disabled={!graph || busy}>
              🧭 Réorganiser
            </button>
          </div>
          {/* Outside .actions: the save button never scrolls out of
              view, whatever the toolbar overflow does. */}
          <button
            className={`flow-save${dirty ? " flow-save--dirty" : ""}`}
            type="button"
            onClick={() => void save()}
            disabled={!dirty || busy}
            title={dirty ? "Des modifications ne sont pas enregistrées" : "Tout est enregistré"}
          >
            {dirty ? "💾 Enregistrer" : "✓ Enregistré"}
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        {overrideFlowId ? (
          <div className="flow-override-banner">
            <span>
              ⚡ <strong>Activation manuelle</strong> :{" "}
              <em>{flows?.find((f) => f.id === overrideFlowId)?.name ?? "(flux supprimé)"}</em>{" "}
              — la programmation horaire est ignorée jusqu'à désactivation.
            </span>
            <button
              className="ghost"
              type="button"
              onClick={() => void clearOverride()}
              disabled={busy}
            >
              Reprendre la programmation
            </button>
          </div>
        ) : null}

        {graph && <Palette onAdd={onAddNode} busy={busy} />}

        {linking && graph && (
          <div className="flow-linking-banner">
            🎯 Cliquez sur le bloc cible pour relier «{" "}
            <strong>
              #{graph.seqs[linking.sourceId] ?? "?"} · {linking.label}
            </strong>{" "}
            » — <kbd>Échap</kbd> ou clic sur le fond pour annuler.
          </div>
        )}

        {validationProblems.length > 0 && (
          <details className="flow-warnings">
            <summary>
              <strong>⚠ {validationProblems.length} point(s) à finaliser</strong>
            </summary>
            <ul>
              {validationProblems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </details>
        )}

        <div className="flow-canvas-wrap">
          {graph && greetings ? (
            <>
              <Canvas
                graph={graph}
                greetings={greetings}
                selectedId={selectedId}
                linkingSourceId={linking?.sourceId ?? null}
                onSelect={handleCanvasSelect}
                onMove={onMove}
              />
              {selectedNode && !linking && (
                <Inspector
                  nodeId={selectedId!}
                  node={selectedNode}
                  seq={graph.seqs[selectedId!] ?? null}
                  isRoot={selectedId === graph.rootId}
                  targets={targets}
                  greetings={greetings}
                  onChange={(next) => onUpdateNode(selectedId!, next)}
                  onStartLink={startLink}
                  onClose={() => setSelectedId(null)}
                  onDelete={() => onDeleteNode(selectedId!)}
                />
              )}
            </>
          ) : (
            <div className="empty">Chargement…</div>
          )}
        </div>
      </section>
    </div>
  );
}

/** Replace every reference to `removedId` in `node` with `replacement`.
 *  Pass "" to clear (when no sensible auto-rewire exists). */
function retargetReferences(node: FlowNode, removedId: string, replacement: string): FlowNode {
  switch (node.kind) {
    case "start":
    case "play":
    case "collect":
      return node.next === removedId ? { ...node, next: replacement } : node;
    case "menu": {
      const m = { ...node, branches: { ...node.branches } };
      for (const k of Object.keys(m.branches)) {
        if (m.branches[k] === removedId) m.branches[k] = replacement;
      }
      if (m.onTimeout === removedId) m.onTimeout = replacement;
      if (m.onInvalid === removedId) m.onInvalid = replacement;
      return m;
    }
    case "ring": {
      const r = { ...node };
      if (r.onAnswer === removedId) r.onAnswer = replacement;
      if (r.onNoAnswer === removedId) r.onNoAnswer = replacement;
      if (r.onNoOneOnline === removedId) r.onNoOneOnline = replacement;
      return r;
    }
    case "voicemail":
    case "hangup":
      return node;
  }
}

/** Surface anything that looks broken so the operator sees it before
 *  saving. Doesn't block save — admins might leave something
 *  half-wired and come back to it. */
function validate(g: Graph): string[] {
  const out: string[] = [];
  const ids = new Set(Object.keys(g.nodes));
  if (!g.nodes[g.rootId]) {
    out.push(`Le bloc de départ « ${g.rootId} » n'existe pas.`);
  }
  // Un flux qui ne décroche jamais met le numéro hors service une fois
  // activé (l'appelant entend « numéro non attribué »). Bloquant.
  if (!flowIsAnswerable(g)) {
    out.push(
      "Ce flux ne décroche jamais (aucun bloc Diffuser / Menu / Sonner / Répondeur atteignable depuis le départ). Il ne pourra pas être activé ni servir de repli.",
    );
  }
  for (const [id, n] of Object.entries(g.nodes)) {
    const label = `« #${g.seqs?.[id] ?? "?"} ${KIND_FR[n.kind]} »`;
    switch (n.kind) {
      case "start":
      case "play":
      case "collect":
        if (!n.next) out.push(`${label} n'a pas de bloc suivant.`);
        else if (!ids.has(n.next)) out.push(`${label} pointe vers un bloc inexistant.`);
        break;
      case "menu":
        for (const [d, t] of Object.entries(n.branches)) {
          if (!t) out.push(`${label} : le choix ${d} n'est pas relié.`);
          else if (!ids.has(t)) out.push(`${label} : le choix ${d} pointe vers un bloc inexistant.`);
        }
        if (!n.onTimeout) out.push(`${label} : aucune cible pour « si aucun chiffre ».`);
        if (!n.onInvalid) out.push(`${label} : aucune cible pour « si invalide ».`);
        break;
      case "ring":
        if (!n.onNoAnswer) out.push(`${label} : aucune cible pour « sans réponse ».`);
        if (!n.onNoOneOnline) out.push(`${label} : aucune cible pour « personne en ligne ».`);
        break;
      case "voicemail":
      case "hangup":
        break;
    }
  }
  return out;
}
